/* eslint jsx-a11y/no-noninteractive-tabindex: ["error", { "roles": ["region"] }] */
import React, { useEffect, useRef, useState } from "react";
import ContentCopy from "@mui/icons-material/ContentCopy";
import Check from "@mui/icons-material/Check";
import "./CodeWindow.css";

function flattenLines(children) {
  return React.Children.toArray(children).flatMap((child) =>
    React.isValidElement(child) && child.type === React.Fragment
      ? flattenLines(child.props.children)
      : [child],
  );
}

function nodeText(node) {
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (React.isValidElement(node))
    return React.Children.toArray(node.props.children).map(nodeText).join("");
  return "";
}

export function CodeWindow({ fileName, children, variant }) {
  const [copyStatus, setCopyStatus] = useState("");
  const timer = useRef(null);
  useEffect(() => {
    setCopyStatus("");
    return () => window.clearTimeout(timer.current);
  }, [fileName]);
  if (variant !== "editor")
    return (
      <div className="code-window">
        <div className="window-header">
          <div className="window-controls">
            <div className="control red" />
            <div className="control yellow" />
            <div className="control green" />
          </div>
          <div className="file-name">{fileName}</div>
        </div>
        <div className="code-content">{children}</div>
      </div>
    );

  const lines = flattenLines(children).map((child) => {
    const classes = React.isValidElement(child)
      ? (child.props.className || "").split(" ")
      : [];
    const indent = classes.includes("indent-3")
      ? 3
      : classes.includes("indent-2")
        ? 2
        : classes.includes("indent")
          ? 1
          : 0;
    return {
      content: React.isValidElement(child) ? child.props.children : child,
      indent,
    };
  });
  const source = lines
    .map((line) => "  ".repeat(line.indent) + nodeText(<>{line.content}</>))
    .join("\n");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(source);
      setCopyStatus("Copied");
    } catch {
      setCopyStatus("Copy unavailable. Select the code to copy.");
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopyStatus(""), 3500);
  };
  return (
    <div className="code-window workers-editor">
      <div className="editor-chrome">
        <div className="editor-file">
          <span>JS</span>
          <strong>{fileName}</strong>
        </div>
        <span className="editor-copy-status" role="status">
          {copyStatus}
        </span>
        <button
          type="button"
          onClick={copy}
          aria-label={"Copy " + fileName}
          title="Copy code"
        >
          {copyStatus === "Copied" ? <Check /> : <ContentCopy />}
        </button>
      </div>
      <div
        className="editor-scroll"
        tabIndex={0}
        role="region"
        aria-label={fileName + " source code"}
      >
        <pre className="editor-source">
          <code>
            {lines.map((line, index) => (
              <span className="editor-line" key={index}>
                <span className="editor-line-number" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="editor-line-code">
                  {"  ".repeat(line.indent)}
                  {line.content}
                </span>
              </span>
            ))}
          </code>
        </pre>
      </div>
      <div className="editor-statusbar">
        <span>
          <i />
          JavaScript
        </span>
        <span>{lines.length} lines</span>
        <span>UTF-8</span>
        <span>LF</span>
      </div>
    </div>
  );
}
